import { Navigate, Route, Routes } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { LoginPage } from "@/auth/LoginPage";
import { LoadingState } from "@/components/States";
import { AppShell } from "@/layout/AppShell";
import { ComingSoonPage } from "@/features/ComingSoonPage";
import { MorePage } from "@/features/MorePage";
import { AssetDetailPage } from "@/features/assets/AssetDetailPage";
import { AssetsPage } from "@/features/assets/AssetsPage";
import { ContainerDetailPage, ContainersPage } from "@/features/containers/ContainersPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { HistoryPage } from "@/features/history/HistoryPage";
import { LoansPage } from "@/features/loans/LoansPage";
import { OutboxPage } from "@/features/offline/OutboxPage";
import { MaterialListDetailPage } from "@/features/lists/MaterialListDetailPage";
import { MaterialListsPage } from "@/features/lists/MaterialListsPage";
import { PoliciesPage } from "@/features/policies/PoliciesPage";
import { ProjectEstimateDetailPage } from "@/features/estimates/ProjectEstimateDetailPage";
import { ProjectEstimatesPage } from "@/features/estimates/ProjectEstimatesPage";
import { IncomingMessagesPage } from "@/features/messaging/IncomingMessagesPage";
import { OutgoingMessagesPage } from "@/features/messaging/OutgoingMessagesPage";
import { SupplierQuoteDetailPage } from "@/features/quotes/SupplierQuoteDetailPage";
import { SupplierQuotesPage } from "@/features/quotes/SupplierQuotesPage";
import { RfqComparisonPage } from "@/features/rfqs/RfqComparisonPage";
import { RfqDetailPage } from "@/features/rfqs/RfqDetailPage";
import { RfqsPage } from "@/features/rfqs/RfqsPage";
import { ProjectDetailPage } from "@/features/projects/ProjectDetailPage";
import { ProjectSurplusPage } from "@/features/projects/ProjectSurplusPage";
import { ProjectSurplusesPage } from "@/features/projects/ProjectSurplusesPage";
import { ProjectsPage } from "@/features/projects/ProjectsPage";
import { SupplierComparisonPage } from "@/features/suppliers/SupplierComparisonPage";
import { SupplierDetailPage } from "@/features/suppliers/SupplierDetailPage";
import { SuppliersPage } from "@/features/suppliers/SuppliersPage";
import { PlannedOrderPage } from "@/features/purchasing/PlannedOrderPage";
import { PlannedOrdersPage } from "@/features/purchasing/PlannedOrdersPage";
import { SupplierReturnPage } from "@/features/purchasing/SupplierReturnPage";
import { SupplierReturnsPage } from "@/features/purchasing/SupplierReturnsPage";
import { PurchaseOrderPage } from "@/features/purchasing/PurchaseOrderPage";
import { PurchasePlanDetailPage } from "@/features/purchasing/PurchasePlanDetailPage";
import { PurchasePlansPage } from "@/features/purchasing/PurchasePlansPage";
import { PurchasingPage } from "@/features/purchasing/PurchasingPage";
import { ReceivingPage } from "@/features/purchasing/ReceivingPage";
import { BlitzPage } from "@/features/ingestion/BlitzPage";
import { ConversationPage } from "@/features/assistant/ConversationPage";
import { SmartInputPage } from "@/features/ingestion/SmartInputPage";
import { InventoryPage } from "@/features/inventory/InventoryPage";
import { LocationsPage } from "@/features/locations/LocationsPage";
import { ProductDetailPage } from "@/features/products/ProductDetailPage";
import { ProductsPage } from "@/features/products/ProductsPage";
import { SearchPage } from "@/features/search/SearchPage";

export function App() {
  const { status } = useAuth();

  if (status === "loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas">
        <LoadingState label="Ouverture de HD-Vault…" />
      </div>
    );
  }

  if (status === "anonymous") return <LoginPage />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:productId" element={<ProductDetailPage />} />

        <Route path="/locations" element={<LocationsPage />} />
        <Route path="/locations/:locationId" element={<LocationsPage />} />
        <Route path="/containers" element={<ContainersPage />} />
        <Route path="/containers/:containerId" element={<ContainerDetailPage />} />

        <Route path="/history" element={<HistoryPage />} />
        <Route path="/sync" element={<OutboxPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/more" element={<MorePage />} />

        {/* Modules that are planned but not built. They say so rather than
            showing an empty screen that looks broken. */}
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/assets/:assetId" element={<AssetDetailPage />} />
        <Route path="/loans" element={<LoansPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="/surplus" element={<ProjectSurplusesPage />} />
        <Route path="/surplus/:surplusId" element={<ProjectSurplusPage />} />
        <Route path="/lists" element={<MaterialListsPage />} />
        <Route path="/lists/:listId" element={<MaterialListDetailPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route path="/estimates" element={<ProjectEstimatesPage />} />
        <Route path="/estimates/:estimateId" element={<ProjectEstimateDetailPage />} />
        <Route path="/rfqs" element={<RfqsPage />} />
        <Route path="/rfqs/:rfqId" element={<RfqDetailPage />} />
        <Route path="/rfqs/:rfqId/comparison" element={<RfqComparisonPage />} />
        <Route path="/quotes" element={<SupplierQuotesPage />} />
        <Route path="/quotes/:quoteId" element={<SupplierQuoteDetailPage />} />
        <Route path="/messages/outgoing" element={<OutgoingMessagesPage />} />
        <Route path="/messages/incoming" element={<IncomingMessagesPage />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        {/* Avant la fiche : « comparison » n'est pas un identifiant, et une
            route paramétrée déclarée d'abord l'avalerait. */}
        <Route path="/suppliers/comparison" element={<SupplierComparisonPage />} />
        <Route path="/suppliers/:supplierId" element={<SupplierDetailPage />} />
        <Route path="/purchasing" element={<PurchasingPage />} />
        <Route path="/purchase-orders/:orderId" element={<PurchaseOrderPage />} />
        <Route path="/commandes" element={<PlannedOrdersPage />} />
        <Route path="/commandes/:orderId" element={<PlannedOrderPage />} />
        <Route path="/retours-fournisseur" element={<SupplierReturnsPage />} />
        <Route path="/retours-fournisseur/:returnId" element={<SupplierReturnPage />} />
        <Route path="/receiving" element={<ReceivingPage />} />
        <Route path="/purchase-plans" element={<PurchasePlansPage />} />
        <Route path="/purchase-plans/:planId" element={<PurchasePlanDetailPage />} />
        <Route path="/alerts/*" element={<ComingSoonPage />} />
        <Route path="/settings/*" element={<ComingSoonPage />} />
        <Route path="/inventory/add" element={<SmartInputPage />} />
        <Route path="/scan" element={<SmartInputPage />} />
        <Route path="/blitz" element={<BlitzPage />} />
        <Route path="/assistant" element={<ConversationPage />} />
        <Route path="/assistant/:conversationId" element={<ConversationPage />} />
        <Route path="/help" element={<ComingSoonPage />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
